import { createContext, useContext } from 'react';
import type { MediaRef } from './presentation';

/**
 * Images d'un formulaire (ADR-146 addendum (16), ADR-106) : une référence de médiathèque (`file_id` ou
 * `common_key`, jamais une URL) devient une adresse servie par le contrôle d'accès des fichiers. Les images
 * d'énumération portées par le modèle (`validation.enum_images`, l'« image de l'objet ») arrivent avec la
 * résolution du Work Item ; d'ici là, rien (la tuile montre son libellé seul).
 *
 * En attendant la médiathèque commune, les clés communes connues se servent depuis des images de
 * démonstration locales (`/demo-media/<clé>.svg`) : aucune adresse externe (egress, R3).
 */
export interface FormMedia {
  /** Adresse d'une référence de médiathèque, ou `undefined` (image inconnue : pas d'image). */
  resolve: (ref: MediaRef | undefined) => string | undefined;
  /** Images d'énumération du modèle, par `Type.champ` puis par valeur (`enum_images`). */
  enumImages?: Readonly<Record<string, Readonly<Record<string, MediaRef>>>>;
  /** Photo de l'opérateur connecté (adresse servie par l'API), si elle existe. */
  operatorPhoto?: string;
  /** Environnement du poste montré dans le bandeau (« Développement », « Test ») ; absent en production. */
  environment?: string;
}

/** Clés communes dont une image de démonstration est livrée avec le web (silhouettes, pictogrammes). */
export const DEMO_COMMON_KEYS: ReadonlySet<string> = new Set([
  'symbols.delivery_van',
  'animals.chamois',
  'animals.fallow_deer',
  'animals.red_deer',
  'animals.wild_boar',
  'animals.roe_deer',
]);

export function demoMediaUrl(ref: MediaRef | undefined): string | undefined {
  const key = ref?.common_key;
  return key !== undefined && DEMO_COMMON_KEYS.has(key) ? `/demo-media/${key}.svg` : undefined;
}

export const DEFAULT_FORM_MEDIA: FormMedia = { resolve: demoMediaUrl };

export const FormMediaContext = createContext<FormMedia>(DEFAULT_FORM_MEDIA);

export function useFormMedia(): FormMedia {
  return useContext(FormMediaContext);
}

/** Image d'une option : celle déclarée par le formulaire, sinon celle de l'objet (`object_image`). */
export function optionImageUrl(media: FormMedia, declared: MediaRef | undefined, sourceKey: string | undefined, value: string): string | undefined {
  const own = media.resolve(declared);
  if (own !== undefined) return own;
  if (sourceKey === undefined) return undefined;
  return media.resolve(media.enumImages?.[sourceKey]?.[value]);
}

/**
 * Images d'énumération servies avec la tâche (work-item-resolution 2.6.0, `option_images`, ADR-146 addendum (17)),
 * clés « partie.champ » du formulaire, rangées par la donnée du modèle d'où viennent les choix (`Type.champ`).
 */
export function enumImagesOf(template: { sections: ReadonlyArray<{ section_id: string; fields: ReadonlyArray<unknown> }> }, optionImages: Readonly<Record<string, Readonly<Record<string, MediaRef>>>> | undefined): Record<string, Readonly<Record<string, MediaRef>>> {
  const out: Record<string, Readonly<Record<string, MediaRef>>> = {};
  if (optionImages === undefined) return out;
  for (const section of template.sections) {
    for (const raw of section.fields) {
      const entry = raw as { field: { field_id: string }; presentation?: { option_source?: { object_type?: string; field_id?: string } } };
      const images = optionImages[`${section.section_id}.${entry.field.field_id}`];
      const source = entry.presentation?.option_source;
      if (images === undefined || source?.object_type === undefined || source.field_id === undefined) continue;
      const key = `${source.object_type}.${source.field_id}`;
      out[key] = { ...(out[key] ?? {}), ...images };
    }
  }
  return out;
}
